import type { Engine } from 'babylonjs';
import type { PreviewView, FullscreenViewState } from './types';
import fullscreenIcon from '@assets-shared/icons/fullscreen.svg';
import swipeIcon from '@assets-shared/icons/swipe.png';
import { createPreviewInteractionTooltip } from './PreviewInteractionTooltip';

// ============================================================================
// FULLSCREEN CONTROLLER
// ============================================================================
// Handles fullscreen expand/collapse animations and UI elements.
// Focused single responsibility: fullscreen behavior only.

const ANIMATION_DURATION: number = 300;
const PREVIEW_CLOSE_BUTTON_STYLE_ID = 'preview-close-button-styles';

/**
 * Create default fullscreen state
 */
export function createFullscreenState(): FullscreenViewState {
  return {
    isFullscreen: false,
    isAnimating: false,
    originalStyles: {},
    originalCanvasWidth: 0,
    originalCanvasHeight: 0,
    originalRect: null,
  };
}

/**
 * Controller for fullscreen expand/collapse functionality
 */
export class FullscreenController {
  private escapeKeyListenerActive = false;
  private onCollapseRequest: ((canvas: HTMLCanvasElement) => void) | null = null;

  /**
   * Set callback for when collapse is requested (e.g., escape key)
   */
  setCollapseRequestHandler(handler: (canvas: HTMLCanvasElement) => void): void {
    this.onCollapseRequest = handler;
  }

  /**
   * Expand a canvas to fullscreen with animation
   */
  expand(
    canvas: HTMLCanvasElement,
    view: PreviewView,
    engine: Engine,
    onComplete?: () => void
  ): void {
    const { fullscreen } = view;
    if (fullscreen.isFullscreen || fullscreen.isAnimating) return;

    fullscreen.isAnimating = true;

    // Store original state
    fullscreen.originalRect = canvas.getBoundingClientRect();
    fullscreen.originalCanvasWidth = canvas.width;
    fullscreen.originalCanvasHeight = canvas.height;
    fullscreen.originalStyles = {
      position: canvas.style.position,
      top: canvas.style.top,
      left: canvas.style.left,
      width: canvas.style.width,
      height: canvas.style.height,
      zIndex: canvas.style.zIndex,
      transform: canvas.style.transform,
      transition: canvas.style.transition,
    };

    const rect = fullscreen.originalRect;

    // Hide expand button
    this.hideExpandButton(view);

    // Black overlay under canvas so iOS Safari shows black in navbar/address bar
    const overlay = this.createFullscreenOverlay();
    view.fullscreenOverlay = overlay;
    document.body.insertBefore(overlay, document.body.firstChild);

    // Setup escape key listener
    this.setupEscapeKeyListener(canvas);

    // Helper to finalize fullscreen state
    const finalize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      engine.resize();
      
      fullscreen.isAnimating = false;
      fullscreen.isFullscreen = true;
      canvas.style.transition = '';
      
      this.showFullscreenUI(view, canvas);
      onComplete?.();
    };

    // Instant mode (no animation)
    if (ANIMATION_DURATION === 0) {
      canvas.style.transition = 'none';
      canvas.style.position = 'fixed';
      canvas.style.top = '0px';
      canvas.style.left = '0px';
      canvas.style.width = '100vw';
      canvas.style.height = '100vh';
      canvas.style.zIndex = '999';
      finalize();
      return;
    }

    // Animated mode
    canvas.style.transition = 'none';
    canvas.style.position = 'fixed';
    canvas.style.top = `${rect.top}px`;
    canvas.style.left = `${rect.left}px`;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    canvas.style.zIndex = '999';

    // Use double rAF to ensure initial position is painted before animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        canvas.style.transition = `top ${ANIMATION_DURATION}ms ease-out, left ${ANIMATION_DURATION}ms ease-out, width ${ANIMATION_DURATION}ms ease-out, height ${ANIMATION_DURATION}ms ease-out`;
        canvas.style.top = '0px';
        canvas.style.left = '0px';
        canvas.style.width = '100vw';
        canvas.style.height = '100vh';

        setTimeout(finalize, ANIMATION_DURATION);
      });
    });
  }

  /**
   * Collapse from fullscreen with animation
   */
  collapse(
    canvas: HTMLCanvasElement,
    view: PreviewView,
    engine: Engine,
    onComplete?: () => void
  ): void {
    const { fullscreen } = view;
    if (!fullscreen.isFullscreen || fullscreen.isAnimating) return;
    if (!fullscreen.originalRect) return;

    fullscreen.isAnimating = true;
    fullscreen.isFullscreen = false;

    // Hide fullscreen UI immediately
    this.hideFullscreenUI(view);

    // Remove black overlay
    if (view.fullscreenOverlay?.parentNode) {
      view.fullscreenOverlay.remove();
    }
    view.fullscreenOverlay = null;

    const rect = fullscreen.originalRect;

    // Helper to finalize collapse state
    const finalize = () => {
      canvas.style.position = fullscreen.originalStyles.position || '';
      canvas.style.top = fullscreen.originalStyles.top || '';
      canvas.style.left = fullscreen.originalStyles.left || '';
      canvas.style.width = fullscreen.originalStyles.width || '';
      canvas.style.height = fullscreen.originalStyles.height || '';
      canvas.style.zIndex = fullscreen.originalStyles.zIndex || '';
      canvas.style.transform = fullscreen.originalStyles.transform || '';
      canvas.style.transition = fullscreen.originalStyles.transition || '';

      canvas.width = fullscreen.originalCanvasWidth;
      canvas.height = fullscreen.originalCanvasHeight;
      engine.resize();

      fullscreen.isAnimating = false;
      this.showExpandButton(view);
      onComplete?.();
    };

    // Instant mode (no animation)
    if (ANIMATION_DURATION === 0) {
      finalize();
      return;
    }

    // Animated mode
    canvas.style.transition = `top ${ANIMATION_DURATION}ms ease-out, left ${ANIMATION_DURATION}ms ease-out, width ${ANIMATION_DURATION}ms ease-out, height ${ANIMATION_DURATION}ms ease-out`;

    // Force reflow
    canvas.offsetHeight;

    // Animate back to original position
    canvas.style.top = `${rect.top}px`;
    canvas.style.left = `${rect.left}px`;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    setTimeout(finalize, ANIMATION_DURATION);
  }

  /**
   * Check if a view is in fullscreen mode
   */
  isFullscreen(view: PreviewView): boolean {
    return view.fullscreen.isFullscreen;
  }

  // ===========================================================================
  // Loading Overlay
  // ===========================================================================

  /**
   * Create loading overlay for a canvas (shows grey background with spinner)
   */
  createLoadingOverlay(canvas: HTMLCanvasElement): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = 'preview-loading-overlay';
    overlay.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #1a1a1c;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 70;
      transition: opacity 300ms ease-out;
    `;

    // Spinner
    const spinner = document.createElement('div');
    spinner.className = 'preview-loading-spinner';
    spinner.style.cssText = `
      width: 32px;
      height: 32px;
      border: 3px solid rgba(255, 255, 255, 0.15);
      border-top-color: rgba(255, 255, 255, 0.6);
      border-radius: 50%;
      animation: preview-spin 0.8s linear infinite;
    `;

    // Add keyframes for spinner animation if not already present
    if (!document.getElementById('preview-loading-styles')) {
      const style = document.createElement('style');
      style.id = 'preview-loading-styles';
      style.textContent = `
        @keyframes preview-spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }

    overlay.appendChild(spinner);

    const parent = canvas.parentElement;
    if (parent) {
      const computedStyle = getComputedStyle(parent);
      if (computedStyle.position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(overlay);
    }

    return overlay;
  }

  /**
   * Hide loading overlay with fade-out
   */
  hideLoadingOverlay(view: PreviewView): void {
    if (view.loadingOverlay) {
      view.loadingOverlay.style.opacity = '0';
      view.loadingOverlay.style.pointerEvents = 'none';
      // Remove after fade completes
      setTimeout(() => {
        if (view.loadingOverlay?.parentNode) {
          view.loadingOverlay.remove();
        }
        view.loadingOverlay = null;
      }, 300);
    }
  }

  // ===========================================================================
  // UI Elements
  // ===========================================================================

  /**
   * Create expand button for a canvas
   */
  createExpandButton(canvas: HTMLCanvasElement, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'preview-expand-btn';
    const icon = document.createElement('img');
    icon.src = fullscreenIcon;
    icon.alt = '';
    icon.setAttribute('aria-hidden', 'true');
    icon.style.width = '20px';
    icon.style.height = '20px';
    icon.style.display = 'block';
    button.appendChild(icon);
    button.style.cssText = `
      position: absolute;
      bottom: 12px;
      right: 12px;
      border: none;
      color: white;
      padding: 10px;
      border-radius: 8px;
      cursor: pointer;
      z-index: 80;
      opacity: 0;
      transition: opacity 200ms ease-out, background 150ms ease, transform 150ms ease;
      pointer-events: none;
    `;
    button.setAttribute('aria-label', 'Expand to fullscreen');

    // Hover effects
    button.onmouseenter = () => { 
      button.style.transform = 'scale(1.05)'; 
    };
    button.onmouseleave = () => { 
      button.style.transform = 'scale(1)'; 
    };

    button.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
    });

    // Position relative to canvas
    const parent = canvas.parentElement;
    if (parent) {
      const computedStyle = getComputedStyle(parent);
      if (computedStyle.position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(button);
    }

    return button;
  }

  /**
   * Create centered interaction tooltip for a canvas
   */
  createExpandTooltip(canvas: HTMLCanvasElement): HTMLDivElement {
    const tooltip = createPreviewInteractionTooltip('Swipe left and right to rotate', swipeIcon);

    const parent = canvas.parentElement;
    if (parent) {
      const computedStyle = getComputedStyle(parent);
      if (computedStyle.position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(tooltip);
    }

    return tooltip;
  }

  /**
   * Create black overlay under fullscreen canvas.
   * On iOS Safari, the navbar/address bar can show page content; this forces black behind them.
   */
  private createFullscreenOverlay(): HTMLDivElement {
    const overlay = document.createElement('div');
    overlay.className = 'preview-fullscreen-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100vw;
      height: 100vh;
      background: #000;
      z-index: 998;
      pointer-events: none;
    `;
    return overlay;
  }

  /**
   * Create fullscreen UI (close button)
   */
  createFullscreenUI(onClose: () => void): HTMLDivElement {
    if (!document.getElementById(PREVIEW_CLOSE_BUTTON_STYLE_ID)) {
      const style = document.createElement('style');
      style.id = PREVIEW_CLOSE_BUTTON_STYLE_ID;
      style.textContent = `
        .preview-close-btn:hover,
        .preview-close-btn:active,
        .preview-close-btn:focus,
        .preview-close-btn:focus-visible {
          background: white !important;
          color: black !important;
          outline: none;
        }
      `;
      document.head.appendChild(style);
    }

    const container = document.createElement('div');
    container.className = 'preview-fullscreen-ui';
    container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      pointer-events: none;
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.2s ease-out;
    `;

    // Close button
    const closeButton = document.createElement('button');
    closeButton.className = 'preview-close-btn';
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>
    `;
    closeButton.style.cssText = `
      position: absolute;
      top: 16px;
      right: 16px;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: transparent;
      border: 2px solid white;
      box-sizing: border-box;
      color: white;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: auto;
      transition: background-color 0.12s ease-out, color 0.12s ease-out;
    `;
    closeButton.addEventListener('click', onClose);

    container.appendChild(closeButton);
    document.body.appendChild(container);

    return container;
  }

  /**
   * Show expand button
   */
  showExpandButton(view: PreviewView): void {
    if (view.expandButton) {
      view.expandButton.style.opacity = '1';
      view.expandButton.style.pointerEvents = 'auto';
    }
    if (view.expandTooltip && !view.expandTooltipDismissed) {
      view.expandTooltip.style.opacity = '1';
    }
  }

  /**
   * Hide expand button
   */
  hideExpandButton(view: PreviewView): void {
    if (view.expandButton) {
      view.expandButton.style.opacity = '0';
      view.expandButton.style.pointerEvents = 'none';
    }
    this.hideExpandTooltip(view);
  }

  /**
   * Hide expand tooltip without affecting button visibility
   */
  hideExpandTooltip(view: PreviewView): void {
    if (view.expandTooltip) {
      view.expandTooltip.style.opacity = '0';
    }
  }

  /**
   * Show fullscreen UI with delayed fade-in
   */
  showFullscreenUI(view: PreviewView, canvas: HTMLCanvasElement): void {
    if (!view.fullscreenUI) {
      view.fullscreenUI = this.createFullscreenUI(() => {
        this.onCollapseRequest?.(canvas);
      });
    }
    // Delayed fade-in after expand animation
    setTimeout(() => {
      if (view.fullscreenUI) {
        view.fullscreenUI.style.opacity = '1';
      }
    }, 50);
  }

  /**
   * Hide fullscreen UI
   */
  hideFullscreenUI(view: PreviewView): void {
    if (view.fullscreenUI) {
      view.fullscreenUI.style.opacity = '0';
    }
  }

  /**
   * Dispose fullscreen UI
   */
  disposeUI(view: PreviewView): void {
    if (view.loadingOverlay) {
      view.loadingOverlay.remove();
      view.loadingOverlay = null;
    }
    if (view.expandButton) {
      view.expandButton.remove();
      view.expandButton = null;
    }
    if (view.expandTooltip) {
      view.expandTooltip.remove();
      view.expandTooltip = null;
    }
    if (view.fullscreenUI) {
      view.fullscreenUI.remove();
      view.fullscreenUI = null;
    }
    if (view.fullscreenOverlay?.parentNode) {
      view.fullscreenOverlay.remove();
    }
    view.fullscreenOverlay = null;
  }

  // ===========================================================================
  // Escape Key Handler
  // ===========================================================================

  private setupEscapeKeyListener(canvas: HTMLCanvasElement): void {
    if (this.escapeKeyListenerActive) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.onCollapseRequest?.(canvas);
      }
    };

    document.addEventListener('keydown', handler);
    this.escapeKeyListenerActive = true;

    // Store handler for cleanup
    (canvas as any).__escapeHandler = handler;
  }

  removeEscapeKeyListener(canvas: HTMLCanvasElement): void {
    const handler = (canvas as any).__escapeHandler;
    if (handler) {
      document.removeEventListener('keydown', handler);
      delete (canvas as any).__escapeHandler;
    }
    this.escapeKeyListenerActive = false;
  }
}
